import { DesignButton, Icon, TextLink, Typography } from 'components';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import {
  shouldShowPractitionerNoteBanner,
  storePractitionerNoteDismissal,
} from 'helpers';
import useCurrentKitAndTinyAccount from 'hooks/useCurrentKitAndTinyAccount';
import { useEffect, useRef, useState } from 'react';
import { usePublishedPractitionerNote } from 'services/practitionerNote/usePublishedPractitionerNote';
import styles from './PractitionerNoteAlert.module.css';

export const PractitionerNoteAlert = () => {
  const { currentKitId } = useCurrentKitAndTinyAccount();
  const { trackEvent } = useAnalytics();
  const [isDismissed, setIsDismissed] = useState(false);
  const hasTrackedView = useRef(false);

  const { data: practitionerNote, isLoading } = usePublishedPractitionerNote(
    currentKitId ?? null,
  );

  // Track 'Patient Note Banner Viewed' once when banner is displayed
  useEffect(() => {
    if (
      !isLoading &&
      practitionerNote &&
      practitionerNote.is_published &&
      currentKitId &&
      !hasTrackedView.current &&
      !isDismissed
    ) {
      trackEvent('Patient Note Banner Viewed', { kit_id: currentKitId });
      hasTrackedView.current = true;
    }
  }, [isLoading, practitionerNote, currentKitId, trackEvent, isDismissed]);

  const handleDismiss = () => {
    if (!currentKitId) return;

    // Track dismissal event
    trackEvent('Patient Note Banner Dismissed', { kit_id: currentKitId });
    // Store dismissal timestamp in localStorage
    storePractitionerNoteDismissal(currentKitId);
    // Hide the banner
    setIsDismissed(true);
  };

  if (isLoading) return null;
  if (!practitionerNote || !practitionerNote.is_published) return null;
  if (isDismissed) return null;

  // Check if banner should be shown based on localStorage timestamps
  if (
    currentKitId &&
    practitionerNote.published_at &&
    !shouldShowPractitionerNoteBanner(
      currentKitId,
      practitionerNote.published_at,
    )
  ) {
    return null;
  }

  return (
    <div className={styles.noteAlert}>
      <div className={styles.leftContent}>
        <div className={styles.iconContainer}>
          <Icon name='file05' size={16} />
        </div>
        <Typography variant='body-s'>
          You have a note from your practitioner
        </Typography>
      </div>
      <div className={styles.rightContent}>
        <TextLink
          to={`/action-plan/${currentKitId}`}
          label='View'
          size='body-s'
        />
        <DesignButton
          variant='wrapper'
          label=''
          iconNameLeft='closeSm'
          onClick={handleDismiss}
        />
      </div>
    </div>
  );
};
